// Column definitions for v3 table views.
//
// Two kinds of columns:
//   - Schema columns: derived from entity_type.fields via getColumnsFor().
//     These represent user-defined domain data (name, email, stage, etc.).
//   - Virtual columns: returned by buildVirtualColumns(). These render
//     system metadata from EntityRecord (id, _meta.created_at) that exists
//     on every entity regardless of schema. Virtual columns are read-only,
//     never appear in entity_type.fields, and should never be added to
//     schemas — they're synthesised by v3 from the EntityRecord envelope.

import type { EntityTypeDefinition, FieldDefinition, FieldType } from "../../lib/types";

export type VirtualColumnType = "virtual-id" | "virtual-timestamp";

export interface TableColumn {
  field: string;
  label: string;
  type: FieldType | VirtualColumnType;
  align: "left" | "right";
  width: string;
  minWidth: number;
  editable: boolean;
  isTitle: boolean;
  choices?: string[];
  target?: string;
  aggregate?: FieldDefinition["aggregate"];
  format?: FieldDefinition["format"];
  monospace?: boolean;
}

const NUMERIC_TYPES: Set<FieldType> = new Set(["number", "currency"]);

const DEFAULT_WIDTHS: Partial<Record<FieldType, string>> = {
  text: "28%",
  email: "22%",
  phone: "18%",
  number: "14%",
  currency: "16%",
  date: "14%",
  datetime: "16%",
  enum: "16%",
  reference: "18%",
  references: "18%",
};

const MIN_WIDTHS: Partial<Record<FieldType, number>> = {
  text: 180,
  email: 220,
  phone: 140,
  number: 110,
  currency: 130,
  date: 110,
  datetime: 160,
  enum: 120,
  reference: 180,
  references: 200,
  richtext: 200,
  document: 200,
};

export function columnMinWidth(field: FieldDefinition): number {
  if (field.type === "text" && (field.display_role === "title" || field.display_field)) {
    return 220;
  }
  return MIN_WIDTHS[field.type] ?? 150;
}

export function getColumnsFor(entityType: EntityTypeDefinition): TableColumn[] {
  const fields = entityType.fields.filter(
    (f) => (f.display_role as string) !== "hidden" && f.type !== "document" && f.type !== "richtext",
  );

  if (fields.length === 0) return [];

  const hasTitleField = fields.some((f) => f.display_role === "title" || f.display_field);

  return fields.map((f, idx) => {
    const isTitle =
      f.display_role === "title" ||
      f.display_field === true ||
      (!hasTitleField && idx === 0 && f.type === "text");

    const isNumeric = NUMERIC_TYPES.has(f.type) || f.display_role === "metric";
    const isRef = f.type === "reference" || f.type === "references";

    return {
      field: f.name,
      label: fieldLabel(f.name),
      type: f.type,
      align: isNumeric ? "right" as const : "left" as const,
      width: DEFAULT_WIDTHS[f.type] ?? "18%",
      minWidth: columnMinWidth(f),
      editable: !isRef,
      isTitle,
      choices: f.choices,
      target: f.target,
      aggregate: f.aggregate,
      format: f.format,
    };
  });
}

export function buildVirtualColumns(): { before: TableColumn[]; after: TableColumn[] } {
  return {
    before: [
      {
        field: "id",
        label: "ID",
        type: "virtual-id",
        align: "left",
        width: "auto",
        minWidth: 140,
        editable: false,
        isTitle: false,
        monospace: true,
      },
    ],
    after: [
      {
        field: "_meta.created_at",
        label: "Created",
        type: "virtual-timestamp",
        align: "left",
        width: "auto",
        minWidth: 120,
        editable: false,
        isTitle: false,
      },
    ],
  };
}

function fieldLabel(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatCellValue(
  value: unknown,
  col: TableColumn,
): string {
  if (value === null || value === undefined || value === "") return "";

  if (col.type === "currency") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (isNaN(n)) return String(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }

  if (col.type === "number") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (isNaN(n)) return String(value);
    if (col.format === "percentage") return `${n}%`;
    if (col.format === "compact_number") {
      return new Intl.NumberFormat("en-US", { notation: "compact" }).format(n);
    }
    return new Intl.NumberFormat("en-US").format(n);
  }

  if (col.type === "date" || col.type === "datetime") {
    return formatDate(String(value), col.type === "datetime");
  }

  return String(value);
}

function formatDate(iso: string, includeTime: boolean): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    if (includeTime) {
      opts.hour = "numeric";
      opts.minute = "2-digit";
    }
    return d.toLocaleDateString("en-US", opts);
  } catch {
    return iso;
  }
}

export function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s()./-]{4,}$/;

export function validateField(
  value: string,
  column: TableColumn,
): string | null {
  if (value === "") return null;

  switch (column.type) {
    case "email":
      return EMAIL_RE.test(value) ? null : "Not a valid email address.";
    case "phone":
      return PHONE_RE.test(value) ? null : "Not a valid phone number.";
    case "number":
    case "currency":
      return isNaN(parseFloat(value.replace(/[,$]/g, ""))) ? "Must be a number." : null;
    case "date":
    case "datetime":
      return isNaN(Date.parse(value)) ? "Not a valid date." : null;
    default:
      return null;
  }
}

export function computeAggregate(
  rows: Record<string, unknown>[],
  col: TableColumn,
): number | null {
  if (!col.aggregate || col.aggregate === "none") return null;

  const nums = rows
    .map((r) => {
      const v = r[col.field];
      return typeof v === "number" ? v : parseFloat(String(v));
    })
    .filter((n) => !isNaN(n));

  if (nums.length === 0) return null;

  switch (col.aggregate) {
    case "sum":
      return nums.reduce((a, b) => a + b, 0);
    case "avg":
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "count":
      return nums.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default:
      return null;
  }
}
